import React, {createContext, useCallback, useContext, useMemo, useRef, useState} from 'react';
import {usePartFormQualificationGate} from '../../part-form-qualification';
import {addCrossSalePartsBatch} from '../adapters/add-part-batch';
import {fetchCrossSalesParts} from '../utils/fetch-cross-sales-parts';
import {enrichCrossSaleParts} from '../utils/enrich-cross-sale-parts';
import {shouldOpenSuggestiveSelling} from '../utils/should-offer-suggestive-selling';
import {getSuggestiveSellingPendingKey} from '../utils/get-suggestive-selling-pending-key';
import SuggestiveSellingDialog from '../components/suggestive-selling-dialog';
import type {
    EnrichedCrossSalePart,
    StagedCrossSaleLine,
    SuggestiveSellingContextValue,
    SuggestiveSellingOfferPayload,
    SuggestiveSellingOfferResult,
    SuggestiveSellingSession,
    SuggestiveSellingTarget,
} from '../types';

const SuggestiveSellingContext = createContext<SuggestiveSellingContextValue | null>(null);

function createInitialSession(
    partId: number,
    partNumber: string,
    partLabel?: string,
    sourceQuantity?: number,
): SuggestiveSellingSession {
    return {
        chain: [{
            partId,
            partNumber,
            partLabel: partLabel ?? partNumber,
            sourceQuantity,
        }],
        visitedPartIds: new Set(),
        depth: 0,
    };
}

export function SuggestiveSellingProvider({children}: {children: React.ReactNode}) {
    const {ensureAllQualified, GateDialogs} = usePartFormQualificationGate();
    const targetRef = useRef<SuggestiveSellingTarget | null>(null);
    const dismissedSourceKeysRef = useRef(new Set<string>());
    const quickOrderSuggestiveSuppressedRef = useRef(false);
    const loadGenerationRef = useRef(0);

    const makeDismissKey = useCallback(
        (targetType: SuggestiveSellingTarget['type'], partId: number) => `${targetType}:${partId}`,
        [],
    );

    const [open, setOpen] = useState(false);
    const [committing, setCommitting] = useState(false);
    const [crossSales, setCrossSales] = useState<EnrichedCrossSalePart[]>([]);
    const [session, setSession] = useState<SuggestiveSellingSession | null>(null);
    const [pendingKeys, setPendingKeys] = useState<Set<string>>(() => new Set());

    const currentSource = useMemo(
        () => session?.chain[session.chain.length - 1] ?? null,
        [session],
    );

    const setKeyPending = useCallback((key: string, pending: boolean) => {
        setPendingKeys((prev) => {
            const next = new Set(prev);
            if (pending) {
                next.add(key);
            } else {
                next.delete(key);
            }
            return next;
        });
    }, []);

    const isPending = useCallback(
        (pendingKey: string) => pendingKeys.has(pendingKey),
        [pendingKeys],
    );

    const clearDismissalsForTargetType = useCallback((targetType: SuggestiveSellingTarget['type']) => {
        for (const key of dismissedSourceKeysRef.current) {
            if (key.startsWith(`${targetType}:`)) {
                dismissedSourceKeysRef.current.delete(key);
            }
        }
        if (targetType === 'quickOrder') {
            quickOrderSuggestiveSuppressedRef.current = false;
        }
    }, []);

    const clearQuickOrderSuggestiveSuppression = useCallback(() => {
        clearDismissalsForTargetType('quickOrder');
    }, [clearDismissalsForTargetType]);

    const isQuickOrderSuggestiveSuppressed = useCallback(
        () => quickOrderSuggestiveSuppressedRef.current,
        [],
    );

    const resetDialog = useCallback(() => {
        loadGenerationRef.current += 1;
        const closingTarget = targetRef.current;
        if (closingTarget?.type === 'quickOrder') {
            if (session?.chain?.length) {
                for (const entry of session.chain) {
                    dismissedSourceKeysRef.current.add(makeDismissKey('quickOrder', entry.partId));
                }
            }
            quickOrderSuggestiveSuppressedRef.current = true;
        }
        setOpen(false);
        setCommitting(false);
        setCrossSales([]);
        setSession(null);
        targetRef.current = null;
    }, [makeDismissKey, session]);

    const loadCrossSales = useCallback(async (
        partId: number,
        partNumber: string,
        partLabel: string | undefined,
        target: SuggestiveSellingTarget,
        nextSession: SuggestiveSellingSession,
        pendingKey: string,
    ): Promise<SuggestiveSellingOfferResult> => {
        const generation = loadGenerationRef.current + 1;
        loadGenerationRef.current = generation;

        setKeyPending(pendingKey, true);

        try {
            const raw = await fetchCrossSalesParts(partId);
            const enriched = await enrichCrossSaleParts(raw, target);

            if (loadGenerationRef.current !== generation) {
                return {opened: false};
            }

            if (!shouldOpenSuggestiveSelling(
                enriched.length,
                partId,
                nextSession.visitedPartIds,
                nextSession.depth,
            )) {
                return {opened: false};
            }

            const sessionWithVisit: SuggestiveSellingSession = {
                ...nextSession,
                visitedPartIds: new Set([...nextSession.visitedPartIds, partId]),
            };

            targetRef.current = target;
            setSession(sessionWithVisit);
            setCrossSales(enriched);
            setOpen(true);
            return {opened: true};
        } finally {
            if (loadGenerationRef.current === generation) {
                setKeyPending(pendingKey, false);
            }
        }
    }, [setKeyPending]);

    const offerAfterAdd = useCallback(async (payload: SuggestiveSellingOfferPayload): Promise<SuggestiveSellingOfferResult> => {
        const {sourcePartId, sourcePartNumber, sourcePartLabel, sourceQuantity, target} = payload;
        if (target.type === 'quickOrder' && quickOrderSuggestiveSuppressedRef.current) {
            return {opened: false};
        }
        if (
            target.type === 'quickOrder'
            && dismissedSourceKeysRef.current.has(makeDismissKey(target.type, sourcePartId))
        ) {
            return {opened: false};
        }
        const pendingKey = getSuggestiveSellingPendingKey(payload);
        const nextSession = createInitialSession(
            sourcePartId,
            sourcePartNumber,
            sourcePartLabel,
            sourceQuantity,
        );
        return loadCrossSales(
            sourcePartId,
            sourcePartNumber,
            sourcePartLabel,
            target,
            nextSession,
            pendingKey,
        );
    }, [loadCrossSales, makeDismissKey]);

    const handleCommit = useCallback(async (stagedLines: StagedCrossSaleLine[]) => {
        const target = targetRef.current;
        const activeSession = session;
        if (!target || !activeSession) {
            return;
        }

        if (!stagedLines.length) {
            resetDialog();
            return;
        }

        const customerId = target.type === 'shoppingCart'
            ? target.customerId
            : target.type === 'quote'
                ? target.customerId
                : target.customerId;

        if (customerId) {
            const canProceed = await ensureAllQualified(
                customerId,
                stagedLines.map((line) => ({
                    partId: line.partId,
                    partNumber: line.partNumber,
                    partLabel: line.partNumber,
                })),
            );
            if (!canProceed) {
                return;
            }
        }

        setCommitting(true);
        const warehouseCode = target.type === 'quote'
            ? target.warehouseCode
            : target.type === 'quickOrder'
                ? target.defaultWarehouseCode
                : undefined;

        const success = await addCrossSalePartsBatch(
            target,
            stagedLines.map((line) => ({
                partId: line.partId,
                partNumber: line.partNumber,
                quantity: line.quantity,
                warehouseCode,
            })),
        );
        setCommitting(false);

        if (!success) {
            return;
        }

        resetDialog();
    }, [ensureAllQualified, resetDialog, session]);

    const contextValue = useMemo<SuggestiveSellingContextValue>(() => ({
        offerAfterAdd,
        isPending,
        isOpen: open,
        isQuickOrderSuggestiveSuppressed,
        clearQuickOrderSuggestiveSuppression,
        clearSuggestiveDismissalsForTarget: clearDismissalsForTargetType,
    }), [clearDismissalsForTargetType, clearQuickOrderSuggestiveSuppression, isQuickOrderSuggestiveSuppressed, isPending, offerAfterAdd, open]);

    return (
        <SuggestiveSellingContext.Provider value={contextValue}>
            {children}
            {GateDialogs}
            <SuggestiveSellingDialog
                open={open}
                onClose={resetDialog}
                committing={committing}
                crossSales={crossSales}
                sourcePartNumber={currentSource?.partNumber ?? ''}
                sourceDefaultQuantity={currentSource?.sourceQuantity}
                chainDepth={session?.depth ?? 0}
                onCommit={handleCommit}
                disableActions={committing}
            />
        </SuggestiveSellingContext.Provider>
    );
}

export function useSuggestiveSellingContext(): SuggestiveSellingContextValue {
    const context = useContext(SuggestiveSellingContext);
    if (!context) {
        throw new Error('useSuggestiveSelling must be used within SuggestiveSellingProvider');
    }
    return context;
}
